// filterView/mountFilterViews.js — puts the filter views onto the documents.php page (plan documents_admin-search,
// phase filter-tabs). Markup contract (documents_ui_helpers.php renderAdminFilterViewPanels):
//
//   <div data-filter-view-state>
//     <script type="application/json" data-filter-view-schema>{…DocumentFilterSchema…}</script>
//     <input type="hidden" name="fv" value="…submitted list…">
//   </div>
//   <div data-filter-view="builder"></div>        one host per variant, each in its own tab panel
//
// The legacy page replaces #filterTable after every AJAX load (documents.js loadDocumentData): a MutationObserver
// sees the new state element and mounts again from its fv; the old views go with the old markup. documents.js leaves
// everything inside [data-filter-view] alone (its selectmenu / reset / text handlers carry .not exceptions).
// The reset button sets the list back to the schema start.
//
// A submitted list that did not fit (old bookmark, crafted URL) found nothing on the server; fromParam drops the same
// conditions (its checks are the server's or stricter), so a note at the top of every view host says so — no viewError
// through the legacy rendering needed. Enter on a select of the views submits the form, as the selectmenus of tab 1 do;
// with the form's first submit button, since documents.js reads e.originalEvent.submitter.classList.
//
// createFilterViewMounter({ root, variants, resetTrigger }) → { destroy }

import { createDisposers } from '../core/disposers.js';
import { createFilterView } from './filterView.js';
import { createSet } from './conditionSet.js';
import { createBuilderView } from './variants/builderView.js';
import { createFormView } from './variants/formView.js';
import { createChipsView } from './variants/chipsView.js';

const VARIANTS = { chips: createChipsView, builder: createBuilderView, form: createFormView };
const STATE = '[data-filter-view-state]';

// The note for what fromParam dropped, or '' — the whole list (unreadable, other version) or single conditions.
function noteText(schema, problems) {
    if (!problems.length) {
        return '';
    }
    if (problems.some((p) => p.reason === 'json' || p.reason === 'version')) {
        return schema.texts.FILTER_VIEW_RESET;
    }
    return schema.texts.FILTER_VIEW_DROPPED.replace('{n}', problems.length);
}

export function createFilterViewMounter({ root = document, variants = VARIANTS, resetTrigger = '#filter_reset' } = {}) {
    const disposers = createDisposers();
    const doc = root.ownerDocument || root;
    let current = null;   // { state, view }

    const unmount = () => {
        current?.view.destroy();
        current = null;
    };
    disposers.add(unmount);

    const mount = () => {
        const state = root.querySelector(STATE);
        if (current && current.state === state) {
            return;
        }
        unmount();
        if (!state) {
            return;
        }
        const script = state.querySelector('script[data-filter-view-schema]');
        const input = state.querySelector('input[name="fv"]');
        if (!script || !input) {
            console.error('[filterView] state without schema or fv input', state);   // markup error, visible
            return;
        }
        const schema = JSON.parse(script.textContent);
        const view = createFilterView({ schema, input });
        const note = noteText(schema, view.problems);
        for (const host of root.querySelectorAll('[data-filter-view]')) {
            const createVariant = variants[host.dataset.filterView];
            if (createVariant) {
                view.mount(createVariant, host);
            } else {
                console.error('[filterView] unknown variant', host.dataset.filterView, host);
            }
            if (note) {
                const el = doc.createElement('div');
                el.className = 'topo2-filterview-note';
                el.setAttribute('role', 'status');
                el.textContent = note;
                host.prepend(el);
            }
        }
        current = { state, view, schema };
    };

    const observer = new MutationObserver(() => {
        // cheap: only a replaced (or removed) state element means work
        if (current?.state.isConnected && root.querySelector(STATE) === current.state) {
            return;
        }
        mount();
    });
    observer.observe(root === doc ? doc.body : root, { childList: true, subtree: true });
    disposers.add(() => observer.disconnect());

    const onClick = (ev) => {
        if (current && ev.target.closest(resetTrigger)) {
            current.view.store.set(createSet(current.schema));
        }
    };
    doc.addEventListener('click', onClick);
    disposers.add(() => doc.removeEventListener('click', onClick));

    const onKeydown = (ev) => {
        const select = ev.key === 'Enter' ? ev.target.closest?.('[data-filter-view] select') : null;
        const form = select?.form;
        if (form) {
            ev.preventDefault();
            form.requestSubmit(form.querySelector('button[type="submit"], input[type="submit"]'));
        }
    };
    doc.addEventListener('keydown', onKeydown);
    disposers.add(() => doc.removeEventListener('keydown', onKeydown));

    mount();
    return {
        destroy() {
            disposers.dispose();
        },
    };
}
